import { defineVariants } from "@/runtime/style-engine/define-variants";
import {
  dropdownRootConfig,
  dropdownContentConfig,
  dropdownGroupConfig,
} from "@/runtime/style-engine/shared-dropdown-styles";

/**
 * Tailwind-like class variants for `<e-select>`.
 */
export const selectClasses = defineVariants(dropdownRootConfig);

/**
 * Tailwind-like class variants for `<e-select-trigger>`.
 */
export const selectTriggerClasses = defineVariants({
  base: `
    inline-flex! flex-row! items-center! justify-between! gap-2!
    cursor-pointer
    outline-none
    transition-colors
    text-left
    min-w-[160px]
  `,
  icon: `flex-shrink-0 text-inherit`,
  content: `flex-1 truncate`,
  placeholder: `text-(--color-foreground)/50`,
  indicator: `flex-shrink-0 text-inherit transition-transform`,

  variants: {
    variant: {
      solid: `hover:bg-(--card-color)/75`,
      outline: `hover:bg-(--card-color)/5`,
      soft: `hover:bg-(--card-color)/15`,
      subtle: `hover:bg-(--card-color)/25`,
      ghost: `hover:bg-(--card-color)/5`,
    },

    size: {
      xs: `px-2! h-6! text-xs rounded!`,
      sm: `px-2.5! h-8! text-sm rounded-md!`,
      md: `px-3! h-10! text-base rounded-lg!`,
      lg: `px-4! h-12! text-lg rounded-lg!`,
      xl: `px-5! h-14! text-xl rounded-lg!`,
    },

    open: {
      true: `ring-2 ring-(--color-foreground)`,
      false: ``,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: `focus-visible:ring-2 focus-visible:ring-(--color-foreground)`,
    },

    multiple: {
      true: `flex-wrap`,
      false: ``,
    },

    block: {
      true: `w-full!`,
      false: `w-auto!`,
    },
  },

  defaults: {
    variant: "subtle",
    size: "md",
    open: "false",
    disabled: "false",
    multiple: "false",
    block: "false",
  },

  compoundVariants: [
    // Open state - rotate indicator
    {
      open: "true",
      class: {
        indicator: `rotate-180`,
      },
    },
    { multiple: "true", size: "xs", class: "h-auto! min-h-6! py-1!" },
    { multiple: "true", size: "sm", class: "h-auto! min-h-8! py-1.5!" },
    { multiple: "true", size: "md", class: "h-auto! min-h-10! py-1.5!" },
    { multiple: "true", size: "lg", class: "h-auto! min-h-12! py-2!" },
    { multiple: "true", size: "xl", class: "h-auto! min-h-14! py-2.5!" },
  ],
});

/**
 * Tailwind-like class variants for `<e-select-content>`.
 */
export const selectContentClasses = defineVariants(dropdownContentConfig);

/**
 * Tailwind-like class variants for `<e-select-item>`.
 * Note: Most styling comes from the underlying e-listbox-item.
 */
export const selectItemClasses = defineVariants({
  base: ``,

  variants: {
    disabled: {
      true: ``,
      false: ``,
    },
  },

  defaults: {
    disabled: "false",
  },
});

/**
 * Tailwind-like class variants for `<e-select-group>`.
 * Note: Most styling comes from the underlying e-listbox-group.
 */
export const selectGroupClasses = defineVariants(dropdownGroupConfig);
